// Standardized confirm dialog. Modal with backdrop, click-outside to dismiss,
// Cancel + Confirm buttons. Used wherever you'd reach for window.confirm()
// but want a styled experience.
//
// Built on the shared ui/dialog (Radix) primitive so it inherits the app's
// one modal system: focus-trap, Escape-to-close, scrim, and animations. The
// controlled props (open / onConfirm / onCancel) are unchanged, so every call
// site works untouched.

import type { ReactNode } from 'react';
import { Button } from './ui/button.js';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog.js';

interface Props {
  open: boolean;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'destructive';
  isPending?: boolean;
  error?: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  isPending = false,
  error,
  onConfirm,
  onCancel,
}: Props) {
  return (
    <Dialog
      open={open}
      // Escape / backdrop / X all route here. House rule: dismiss on Escape —
      // but never while a confirm is in flight, so a stray dismiss can't abandon
      // a pending write (matches the prior useEscapeKey guard, now also covering
      // the backdrop the old hand-rolled version left unguarded).
      onOpenChange={(next) => {
        if (!next && !isPending) onCancel();
      }}
    >
      <DialogContent
        className="w-80 max-w-[calc(100vw-2rem)] gap-4 p-6"
        onEscapeKeyDown={(e) => {
          if (isPending) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (isPending) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="text-base">{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {error}
          </div>
        )}
        <div className="flex gap-2">
          <Button
            variant={variant === 'destructive' ? 'destructive' : 'default'}
            className="flex-1"
            onClick={onConfirm}
            disabled={isPending}
          >
            {isPending ? 'Working…' : confirmLabel}
          </Button>
          <Button variant="outline" className="flex-1" onClick={onCancel} disabled={isPending}>
            {cancelLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
